import type { ImportReport } from '@servicetim/shared';
import {
  EmptyState,
  Label,
  LoadingState,
  ProblemBanner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@servicetim/ui/components';
import { useId } from 'react';

export interface ReportTabProps {
  report: ImportReport | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  completePartially: boolean;
  onCompletePartiallyChange: (value: boolean) => void;
}

/** S-09-04's dry-run report: counts, unmapped lists, validation errors, oversized attachments. */
export function ReportTab({
  report,
  isPending,
  error,
  onRetry,
  completePartially,
  onCompletePartiallyChange,
}: ReportTabProps) {
  const switchId = useId();
  if (isPending) {
    return <LoadingState rows={4} label="Loading the report…" />;
  }
  if (error) {
    return <ProblemBanner error={error} onRetry={onRetry} />;
  }
  if (!report) {
    return <EmptyState title="No report yet" description="Run a dry run to see one here." />;
  }
  const hasErrors = report.validationErrors.length > 0;

  return (
    <div className="space-y-6">
      {report.blocking ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-red-900 dark:text-red-300 text-sm"
        >
          <p className="font-medium">This report has blocking validation errors.</p>
          <p className="mt-1">
            Fix the mapping, or switch on "complete partially" below to run anyway and skip the
            flagged rows.
          </p>
        </div>
      ) : null}

      <section aria-label="Counts">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tickets</TableHead>
              <TableHead>Comments</TableHead>
              <TableHead>Attachments</TableHead>
              <TableHead>Users</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>{report.counts.tickets}</TableCell>
              <TableCell>{report.counts.comments}</TableCell>
              <TableCell>{report.counts.attachments}</TableCell>
              <TableCell>{report.counts.users}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </section>

      {report.unmappedFields.length > 0 ? (
        <UnmappedList title="Unmapped fields" items={report.unmappedFields} />
      ) : null}
      {report.unmappedStatuses.length > 0 ? (
        <UnmappedList title="Unmapped statuses" items={report.unmappedStatuses} />
      ) : null}
      {report.unmappedUsers.length > 0 ? (
        <UnmappedList title="Unmapped users" items={report.unmappedUsers} />
      ) : null}

      {hasErrors ? (
        <section aria-label="Validation errors" className="space-y-2">
          <h3 className="font-medium text-sm">
            Validation errors ({report.validationErrors.length})
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source id</TableHead>
                <TableHead>Line</TableHead>
                <TableHead>Field</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.validationErrors.map((e, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: sourceId/field/message can repeat across rows
                <TableRow key={`${e.sourceId}:${e.field}:${i}`}>
                  <TableCell className="font-mono text-xs">{e.sourceId}</TableCell>
                  <TableCell>{e.line ?? '—'}</TableCell>
                  <TableCell>{e.field}</TableCell>
                  <TableCell>{e.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ) : null}

      {report.oversizedAttachments.length > 0 ? (
        <section aria-label="Oversized attachments" className="space-y-2">
          <h3 className="font-medium text-sm">
            Oversized attachments ({report.oversizedAttachments.length})
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Filename</TableHead>
                <TableHead>Size</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.oversizedAttachments.map((a) => (
                <TableRow key={a.sourceId}>
                  <TableCell className="font-mono text-xs">{a.ticketSourceId}</TableCell>
                  <TableCell>{a.filename}</TableCell>
                  <TableCell>{Math.round(a.sizeBytes / 1024 / 1024)} MB</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ) : null}

      {hasErrors ? (
        <div className="flex items-center gap-2">
          <Switch
            id={switchId}
            checked={completePartially}
            onCheckedChange={onCompletePartiallyChange}
          />
          <Label htmlFor={switchId}>Complete partially (run anyway, skip the flagged rows)</Label>
        </div>
      ) : null}
    </div>
  );
}

function UnmappedList({
  title,
  items,
}: {
  title: string;
  items: readonly { sourceId: string; label: string; count: number }[];
}) {
  return (
    <section aria-label={title} className="space-y-1">
      <h3 className="font-medium text-sm">
        {title} ({items.length})
      </h3>
      <ul className="list-disc space-y-0.5 pl-5 text-fg-muted text-sm">
        {items.map((u) => (
          <li key={u.sourceId}>
            {u.label} <span className="text-fg-muted text-xs">({u.count} records)</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
